import { useCallback, useEffect, useRef, useState } from "react";
import { config } from "../config";
import {
  computeCodeRangeFromDigits,
  risingToResolvingCode,
} from "./eventCodeRange";
import { getNumberRangeBaseFunctions } from "./numberRangeBasesCache";
import type { NumberRangeBaseFunction, NumberRangeSubFunction } from "./NumberRange";
import { numberRangeDisplayName } from "./NumberRange";

export type NumberRangeOverviewRow = {
  baseId: number;
  baseDigit: number;
  baseLabel: string;
  subId: number;
  subDigit: number;
  subLabel: string;
  risingMin: number;
  risingMax: number;
  resolvingMin: number;
  resolvingMax: number;
  /** Catalog occupancy (rising band only); optional until fill API loads. */
  used?: number;
  free?: number;
  percentFilled?: number;
};

const TTL_MS = 60_000;

let cachedRows: { rows: NumberRangeOverviewRow[]; fetchedAt: number } | null = null;
let inflight: Promise<NumberRangeOverviewRow[]> | null = null;

export function buildOverviewRows(
  bases: NumberRangeBaseFunction[],
  subsByBaseId: Map<number, NumberRangeSubFunction[]>,
  languageCode = "eng"
): NumberRangeOverviewRow[] {
  const rows: NumberRangeOverviewRow[] = [];
  const sortedBases = [...bases].sort((a, b) => a.digit - b.digit || a.id - b.id);

  for (const base of sortedBases) {
    const baseLabel = numberRangeDisplayName(base.descriptions, languageCode);
    if (!baseLabel) continue;

    const subs = [...(subsByBaseId.get(base.id) ?? [])].sort(
      (a, b) => a.digit - b.digit || a.id - b.id
    );

    for (const sub of subs) {
      const subLabel = numberRangeDisplayName(sub.descriptions, languageCode);
      if (!subLabel) continue;
      const { min: risingMin, max: risingMax } = computeCodeRangeFromDigits(
        base.digit,
        sub.digit
      );
      const resolvingMin = risingToResolvingCode(risingMin);
      const resolvingMax = risingToResolvingCode(risingMax);

      rows.push({
        baseId: base.id,
        baseDigit: base.digit,
        baseLabel,
        subId: sub.id,
        subDigit: sub.digit,
        subLabel,
        risingMin,
        risingMax,
        resolvingMin,
        resolvingMax,
      });
    }
  }

  return rows;
}

export async function fetchNumberRangeOverviewRows(
  callApi: (url: string, method?: string) => Promise<unknown>,
  opts?: { force?: boolean }
): Promise<NumberRangeOverviewRow[]> {
  if (opts?.force) {
    cachedRows = null;
    inflight = null;
  }
  if (!opts?.force && cachedRows && Date.now() - cachedRows.fetchedAt < TTL_MS) {
    return cachedRows.rows;
  }
  if (inflight) {
    return inflight;
  }

  inflight = (async () => {
    const bases = await getNumberRangeBaseFunctions((url, method) =>
      callApi(url, method ?? "GET")
    );
    const subsByBaseId = new Map<number, NumberRangeSubFunction[]>();

    await Promise.all(
      bases.map(async (base) => {
        const url = `${config.apiUrl}/api/number-range-sub-function/?base=${base.id}&page_size=100`;
        const res = (await callApi(url, "GET")) as { results?: NumberRangeSubFunction[] };
        const list = Array.isArray(res?.results) ? res.results : [];
        subsByBaseId.set(base.id, list);
      })
    );

    const rows = buildOverviewRows(bases, subsByBaseId);
    cachedRows = { rows, fetchedAt: Date.now() };
    return rows;
  })();

  try {
    return await inflight;
  } finally {
    inflight = null;
  }
}

export function invalidateNumberRangeOverviewCache(): void {
  cachedRows = null;
  inflight = null;
}

export function useNumberRangeOverview(
  callApi: ((url: string, method?: string) => Promise<unknown>) | undefined
) {
  const [rows, setRows] = useState<NumberRangeOverviewRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const callApiRef = useRef(callApi);
  callApiRef.current = callApi;

  const reload = useCallback((force?: boolean) => {
    const api = callApiRef.current;
    if (!api) {
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    void fetchNumberRangeOverviewRows(api, { force })
      .then((data) => setRows(data))
      .catch((e) => {
        console.error("Failed to load number range overview:", e);
        setError("Could not load function overview.");
        setRows([]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!callApi) {
      setRows([]);
      setLoading(false);
      return;
    }
    reload(false);
    // Intentionally only when callApi becomes available — not on every render identity change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Boolean(callApi)]);

  return { rows, loading, error, reload };
}
